/**
 * Settings route: /account/settings
 * No IDs in URL - caller must lookup institutionId from localStorage
 */
type SettingsRoute = {
    type: 'settings';
};

/**
 * Child profile route: /account/childProfile/:childId
 * Has childId only - caller may lookup institutionId from Redux using childId
 */
type ChildProfileRoute = {
    type: 'childProfile';
    childId: string;
};

/**
 * Institution route: /account/institution/:id
 * Has institutionId only
 */
type InstitutionRoute = {
    type: 'institution';
    institutionId: string;
};

/**
 * Organization route: /account/organization/:id
 * Has organizationId only
 */
type OrganizationRoute = {
    type: 'organization';
    organizationId: string;
};

/**
 * Unknown route: No IDs detected (e.g., /account/calendar/me, /account/feed/me)
 */
type UnknownRoute = {
    type: 'unknown';
};

export type ExtractedIds = SettingsRoute | ChildProfileRoute | InstitutionRoute | OrganizationRoute | UnknownRoute;

/**
 * Extracts institution and organization IDs from a URL hash
 * @param url The URL hash to extract IDs from (e.g. "#/account/institution/123/overview" or "#/account/organization/456/settings")
 * @returns Discriminated union based on the route type detected
 */
export const extractIdsFromUrl = (url?: string): ExtractedIds => {
    if (!url) {
        return {type: 'unknown'};
    }

    // Clean the URL by removing the hash prefix if present
    const cleanUrl = url.startsWith('#') ? url.substring(1) : url;

    // 1. Settings route (mutually exclusive with IDs)
    // Detect settings route: /account/settings
    // The settings page doesn't have institutionId in the URL path.
    // The caller should look up institutionId from localStorage when this is true.
    if (/\/account\/settings(?:\/|$|\?)/.test(cleanUrl)) {
        return {type: 'settings'};
    }

    // Extract childId from child profile route: /account/childProfile/:childId
    // - "/account/childProfile/" matches the literal path prefix
    // - ([^/?]+) captures the child ID, stopping at either '/' or '?' characters
    // Examples:
    // - From "/account/childProfile/abc-123/activity" captures "abc-123"
    // - From "/account/childProfile/def-456?tab=overview" captures "def-456"
    const childProfileMatch = cleanUrl.match(/\/account\/childProfile\/([^/?]+)/);
    const childId = childProfileMatch?.[1];

    // 2. Child profile route
    if (childId) {
        return {
            type: 'childProfile',
            childId,
        };
    }

    // Extract institution ID:
    // - "/account/institution/" matches the literal path prefix
    // - ([^/?]+) captures one or more characters that are not forward slashes or question marks
    // Examples:
    // - From "/account/institution/abc-123/overview" captures "abc-123"
    // - From "/account/institution/def-456?param=value" captures "def-456"
    const institutionMatch = cleanUrl.match(/\/account\/institution\/([^/?]+)/);
    const institutionId = institutionMatch?.[1];

    // 3. Institution route
    if (institutionId) {
        return {
            type: 'institution',
            institutionId,
        };
    }

    // Extract organization ID:
    // - "/account/organization/" matches the literal path prefix
    // - ([^/?]+) captures the ID, stopping at either '/' or '?' characters
    // Examples:
    // - From "/account/organization/xyz-789/settings" captures "xyz-789"
    // - From "/account/organization/uvw-012?tab=overview" captures "uvw-012"
    const organizationMatch = cleanUrl.match(/\/account\/organization\/([^/?]+)/);
    const organizationId = organizationMatch?.[1];

    // 4. Organization route
    if (organizationId) {
        return {
            type: 'organization',
            organizationId,
        };
    }

    // 5. Unknown route (no IDs)
    return {type: 'unknown'};
};
